import {
    Grid,
    Button,
    Flex,
    Label,
    useStyles,
    Avatar,
    Caption,
    pxToRem,
} from '@archipro-design/aria';

import * as S from './LocationDetail.mobile.style';
import type {
    ContactLoaderData,
    LocationDataType,
    ProfessionalInitialType,
} from '@modules/professional/type/contact-page-types';
import { HeartFill, HeartLine, MapPinLine } from '@archipro-design/icons';

import {
    getFullAddress,
    getLocationProfessional,
} from '@modules/professional/util/contact-page-helper';
import { useLoaderData, useNavigate } from 'react-router';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import ClickToView from '~/modules/root/component/click-to-view/ClickToView';
import { parsePhone } from '~/modules/root/util/phone';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { isUserLoggedIn, useUser } from '@modules/user';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import { useFormatDistance } from '~/modules/location/hook/use-format-distance';

interface ItemProps {
    location: LocationDataType;
    professional: ProfessionalInitialType;
}

export const LocationDetailMobile: React.FC<ItemProps> = ({
    location,
    professional,
}) => {
    const { css } = useStyles();
    const formatDistance = useFormatDistance();
    const navigate = useNavigate();
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];

    const onViewProfile = (
        e: React.SyntheticEvent<HTMLElement, Event>,
        routePath: string,
    ) => {
        e.preventDefault();
        navigate(routePath);
    };

    const locationProfessional = getLocationProfessional(
        location,
        professional,
        linkedProfiles,
    );

    const phoneParsed = location.Phone
        ? parsePhone(location.Phone, location.Country)
        : null;

    const { isInDesignBoard } = useHandleFollow();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { guestSave } = useGuestSave();

    const { openEnquiryModal } = useEnquiryModal({
        professional: professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        source: 'ProfessionalLocationDetail',
        includeBranch: true,
    });
    const { showEnquiryCTA, showSpecifierLoginPrompt } =
        useEnquiryVisibility(professional);
    const enquiryLabel = 'Enquire';

    return (
        <>
            <Flex className={css(S.LocationDetailWrap)}>
                <div className={css(S.LocationAvatar)}>
                    <Avatar
                        image={{
                            src: locationProfessional.CompanyLogo ?? '',
                            alt: `${locationProfessional.Title} company logo`,
                        }}
                        size={80}
                        imageFit={'contain'}
                        variables={{
                            avatarImageBackgroundColor:
                                locationProfessional.LogoBackgroundColor,
                            borderWidth: '0',
                            avatarBackgroundColor:
                                locationProfessional.LogoBackgroundColor,
                            borderRadius: '0',
                        }}
                    />
                </div>

                <Grid columns={1}>
                    <Flex
                        space="between"
                        hAlign="end"
                        className={css(S.LocationType)}
                    >
                        <div>{location.Type}</div>
                        {location.distance !== undefined && (
                            <>
                                <Label variant="02">
                                    {formatDistance(location.distance)}
                                </Label>
                                <div className="map-pin">
                                    <MapPinLine size="2x" />
                                </div>
                            </>
                        )}
                    </Flex>

                    <div className={css(S.LocationTitle)}>
                        <Caption variant="03">{location.Title}</Caption>
                    </div>

                    <Flex className={css(S.LocationAddress)}>
                        <ClickToView
                            fulltext={getFullAddress(location)}
                            coordinates={location.MapCoordinates}
                            clickLabel="Address"
                            type={'Location'}
                            label={professional.Title}
                            buttonTrackingID={
                                locationProfessional.PageType.toLowerCase() +
                                '-contact-accordion-address-reveal'
                            }
                            revealKey={`professional-${locationProfessional.ID}`}
                            revealSubKey={location.ID.toString()}
                        />
                    </Flex>
                    <Flex className={css(S.LocationPhone)}>
                        <ClickToView
                            fulltext={location.Phone || ''}
                            telHref={phoneParsed?.telUri}
                            clickLabel="Phone number"
                            type={'Phone'}
                            label={professional.Title}
                            buttonTrackingID={
                                locationProfessional.PageType.toLowerCase() +
                                '-library-location-phone-reveal'
                            }
                            revealKey={`professional-${locationProfessional.ID}-${location.ID}`}
                            // The page's professional, not the location's. A
                            // branch location can belong to a linked profile,
                            // and the ad that brought the visitor here was for
                            // the page — crediting the branch would leave the
                            // lead site-wide.
                            professionalId={professional.ID}
                        />
                    </Flex>

                    {location.AllowsPickup && (
                        <Flex className={css(S.ClickAndCollectAvailable)}>
                            <Label>Click & collect available</Label>
                        </Flex>
                    )}

                    <Grid
                        columns={`1fr ${pxToRem(48)} 1fr`}
                        className={css(S.EnquiryAndSaveWrapper)}
                    >
                        <div>
                            {showEnquiryCTA && (
                                <Button
                                    color="dark"
                                    size={24}
                                    className={css(S.EnquireButton)}
                                    variables={{
                                        darkButtonBackgroundColorNormal:
                                            '#222222',
                                    }}
                                    onClick={() =>
                                        openEnquiryModal({
                                            title: enquiryLabel,
                                            ctaLabel: enquiryLabel,
                                        })
                                    }
                                >
                                    {enquiryLabel}
                                </Button>
                            )}
                        </div>
                        {(() => {
                            const isSaved = isInDesignBoard(
                                locationProfessional.ID,
                                'ProfessionalPin',
                            );
                            const heartButton = (
                                <Button
                                    transparent
                                    size={16}
                                    className={css(S.SaveToDesignBoardButton)}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (!isLoggedIn) {
                                            guestSave({
                                                pinId: locationProfessional.ID,
                                                pinType: 'ProfessionalPin',
                                            });
                                        }
                                    }}
                                    icon={
                                        isSaved ? (
                                            <HeartFill size="1x" />
                                        ) : (
                                            <HeartLine size="1x" />
                                        )
                                    }
                                />
                            );
                            return isLoggedIn ? (
                                <FollowSelector
                                    trigger={heartButton}
                                    pinId={locationProfessional.ID}
                                    pinType="ProfessionalPin"
                                />
                            ) : (
                                heartButton
                            );
                        })()}

                        <Flex hAlign="center">
                            {locationProfessional.ID !== professional.ID && (
                                <Button
                                    as={'a'}
                                    href={`/professional/${locationProfessional.URLSegment}`}
                                    color="dark"
                                    transparent={true}
                                    size={16}
                                    variables={{
                                        buttonPadding: 0,
                                    }}
                                    className={css(S.ViewProfileButton)}
                                    onClick={(e) =>
                                        onViewProfile(
                                            e,
                                            `/professional/${locationProfessional.URLSegment}`,
                                        )
                                    }
                                >
                                    View Profile
                                </Button>
                            )}
                        </Flex>
                    </Grid>
                    {showSpecifierLoginPrompt && (
                        <div className="mt-4">
                            <SpecifierLoginPrompt
                                professionalName={professional.Title}
                                itemDescription="this professional"
                                showBottomBorder={false}
                                className="py-0"
                            />
                        </div>
                    )}
                </Grid>
            </Flex>
        </>
    );
};
